import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '@/utils/api';

export interface EarnOverview {
  serviceReady: boolean;
  walletAddress: string | null;
  balanceKlv: number | null;
  totals: Record<string, { count: number; sumKlv: number }>;
  lifetimePaidKlv: number;
  last24hPaidKlv: number;
}

export interface EarnConfigRow {
  achievementType: string;
  payoutType: 'fixed_klv' | 'pct_of_fee_from_trigger_tx' | 'pct_of_accumulated_fees';
  payoutValue: number;
  enabled: boolean;
  updatedAt: string;
}

export interface EarnLedgerRow {
  id: string;
  appUserId: string;
  username: string | null;
  achievementType: string;
  awardId: string | null;
  payoutKlv: number;
  payoutType: string;
  txHash: string | null;
  status: 'paid' | 'frozen' | 'failed' | 'cancelled';
  notes: string | null;
  createdAt: string;
}

export interface EarnLedgerQuery {
  status?: EarnLedgerRow['status'];
  achievementType?: string;
  limit?: number;
  offset?: number;
}

export const fetchEarnOverview = createAsyncThunk<EarnOverview>(
  'adminEarn/fetchOverview',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get<EarnOverview>('/admin/achievements/earn/overview');
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? 'Failed to load overview');
    }
  },
);

export const fetchEarnConfig = createAsyncThunk<{ rows: EarnConfigRow[] }>(
  'adminEarn/fetchConfig',
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get<{ rows: EarnConfigRow[] }>('/admin/achievements/earn/config');
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? 'Failed to load config');
    }
  },
);

export const updateEarnConfigRow = createAsyncThunk<
  { row: EarnConfigRow },
  { achievementType: string; enabled?: boolean; payoutValue?: number }
>(
  'adminEarn/updateConfig',
  async ({ achievementType, enabled, payoutValue }, { rejectWithValue }) => {
    try {
      const body: Record<string, unknown> = {};
      if (enabled !== undefined) body.enabled = enabled;
      if (payoutValue !== undefined) body.payoutValue = payoutValue;
      const res = await api.put<{ row: EarnConfigRow }>(
        `/admin/achievements/earn/config/${encodeURIComponent(achievementType)}`,
        body,
      );
      return res.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? 'Failed to update row');
    }
  },
);

export const fetchEarnLedger = createAsyncThunk<
  { rows: EarnLedgerRow[]; total: number; limit: number; offset: number },
  EarnLedgerQuery
>(
  'adminEarn/fetchLedger',
  async (query, { rejectWithValue }) => {
    try {
      const res = await api.get('/admin/achievements/earn/ledger', { params: query });
      return res.data as { rows: EarnLedgerRow[]; total: number; limit: number; offset: number };
    } catch (err: any) {
      return rejectWithValue(err.response?.data?.error ?? 'Failed to load ledger');
    }
  },
);
